import React, { useEffect, useRef } from 'react';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { withRouter } from 'react-router-dom';

import { useStateValue } from '../../Contexto/store';
import { useBusquedaGlobal, useRecientes } from './hooks';
import RecentSearches from './RecentSearches';
import ResultadoLista from './ResultadoLista';
import NoResults from './NoResults';
import { useBuscadorGlobalMobileStyles } from './styles/BuscadorGlobalMobileStyles';

const BuscadorGlobalMobile = ({ open, onClose, history, labels: labelsProps }) => {
  const classes = useBuscadorGlobalMobileStyles();
  const inputRef = useRef(null);
  const [{ sesionUsuario, etiquetasIdioma }] = useStateValue();

  const usuarioId = sesionUsuario?.usuario?.userId;
  const companiaId = sesionUsuario?.usuario?.companyId;
  const idioma = etiquetasIdioma?.CodigoIdioma || "ES";

  // Labels del buscador (preferir props de BarSesion, fallback a store directo)
  const labels = labelsProps || etiquetasIdioma?.EtiquetasComponente?.BuscadorGlobal || {};

  // Hook de búsqueda
  const {
    textoBusqueda,
    resultados,
    cargando,
    textoPrevio,
    manejarCambioTexto,
    limpiarBusqueda,
    manejarItemClick,
    tieneResultados,
    debeMostrarNoResultados,
  } = useBusquedaGlobal({
    usuarioId,
    companiaId,
    idioma,
    onNavigate: (item, esUrlAbsoluta) => {
      if (!item.disabled && item.path) {
        if (esUrlAbsoluta) {
          window.location.href = item.path;
        } else {
          history.push(item.path);
        }
      }
    },
    onGuardarReciente: (item) => {
      if (usuarioId && item.path) {
        guardarReciente(item);
      }
    },
  });

  // Hook de recientes
  const {
    recientes,
    tieneRecientes,
    cargarRecientes,
    guardarReciente,
    eliminarReciente,
    limpiarRecientes,
  } = useRecientes(usuarioId);

  // Auto-focus al abrir
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }

    // Limpiar al cerrar
    if (!open) {
      limpiarBusqueda();
    }
  }, [open, limpiarBusqueda]);

  // Cargar recientes al abrir
  useEffect(() => {
    if (open) {
      cargarRecientes(true);
    }
  }, [open, cargarRecientes]);

  // Manejadores
  const handleClose = () => {
    onClose();
  };

  const manejarMensajeSinPermisos = () => {
    // TODO: Implementar snackbar para mobile si es necesario
  };

  // Wrapper para manejarItemClick
  const handleItemClick = (item) => {
    if (item.disabled) {
      manejarMensajeSinPermisos();
      return;
    }
    manejarItemClick(item);
    onClose();
  };

  // Estados derivados
  const puedeMostrarRecientes = !textoBusqueda && tieneRecientes;

  return (
    <Dialog open={open} onClose={handleClose} fullScreen className={classes.dialog}>
      {/* Header con botón close */}
      <Box className={classes.dialogHeader}>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={handleClose} className={classes.closeButton} size="large">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Input de búsqueda */}
      <Box className={classes.inputWrapper}>
        <Paper className={classes.papelInput} elevation={0}>
          <InputBase
            ref={inputRef}
            className={classes.input}
            placeholder={labels.Et_Placeholder}
            value={textoBusqueda}
            onChange={(e) => manejarCambioTexto(e.target.value)}
            inputProps={{ 'aria-label': 'buscar' }}
          />
          {cargando ? (
            <CircularProgress className={classes.loadingIcon} size={20} thickness={5} />
          ) : (
            <SearchIcon className={classes.searchIcon} />
          )}
        </Paper>
      </Box>

      {/* Contenido */}
      <Box className={classes.dialogContent}>
        {puedeMostrarRecientes && (
          <RecentSearches
            recientes={recientes}
            onItemClick={handleItemClick}
            onEliminarItem={eliminarReciente}
            onLimpiarTodos={limpiarRecientes}
            labels={labels}
          />
        )}

        {tieneResultados && (
          <ResultadoLista resultados={resultados} onItemClick={handleItemClick} labels={labels} />
        )}

        {debeMostrarNoResultados && <NoResults textoBusqueda={textoPrevio} labels={labels} />}

        {!puedeMostrarRecientes && !tieneResultados && !debeMostrarNoResultados && !cargando && (
          <Box className={classes.emptyState}>
            <SearchIcon className={classes.emptyIcon} />
            <Box className={classes.emptyText}>{labels.Et_EscribeParaBuscar}</Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default withRouter(BuscadorGlobalMobile);
